/**
 * CER-59 / CER-58 — when a checklist item may be submitted, and later approved,
 * without a photo.
 *
 * Ordinary items do not need an upload. Clauses in `MANDATORY_CHECKLIST_PHOTOS`
 * still do, and that gate blocks field submit. Dan may later want a photo on
 * every answered item: flip `REQUIRE_PHOTO_ON_EVERY_CHECKLIST_ITEM`. The field
 * app and the submit route both read it, so the two gates stay aligned.
 *
 * The dashboard warning (CER-58) is a different rule. It lists any answered
 * item with no photo except Compliant, Not Sighted (`not_cited`), Not
 * Applicable, N/V, and Minor Non Compliance. It warns; it does not block
 * submit. Medium / Major still warn.
 */
import { z } from 'zod';
import { ASSESSMENT_OPTIONS, assessmentLabelForValue, assessmentValueForClauseResponse, } from './assessment-library.js';
import { ChecklistSectionSchema, } from './field.js';
import { mandatoryChecklistPhotosFor, mandatoryPhotoSatisfiedBy, sectionsCoveredBy, } from './mandatory-photos.js';
/**
 * When true, every answered checklist item needs an evidence photo before the
 * inspector can leave the clause or submit the report.
 *
 * Default false. Mandatory-upload clauses are enforced either way.
 */
export const REQUIRE_PHOTO_ON_EVERY_CHECKLIST_ITEM = false;
export function photoRequiredOnEveryChecklistItem(options) {
    return options?.requirePhotoOnEveryChecklistItem ?? REQUIRE_PHOTO_ON_EVERY_CHECKLIST_ITEM;
}
/** Explicit no-finding exemptions; absence of requiresPhoto does not imply exemption. */
const exemptOptions = Object.values(ASSESSMENT_OPTIONS).filter((option) => option.missingPhotoExempt);
export const MISSING_PHOTO_EXEMPT_ASSESSMENTS = exemptOptions.map((option) => option.value);
export const MISSING_PHOTO_EXEMPT_LABELS = exemptOptions.map((option) => option.label);
const EXEMPT = new Set(MISSING_PHOTO_EXEMPT_ASSESSMENTS);
/** Stored assessments win; legacy outcome-only rows retain their existing resolution. */
export function clauseExemptFromMissingPhotoWarning(response) {
    const assessment = assessmentValueForClauseResponse(response);
    return assessment !== undefined && EXEMPT.has(assessment);
}
/** Manifests win on id; submission photos fill anything not yet uploaded. */
export function mergedEvidencePhotos(primary, fallback) {
    const byId = new Map();
    const add = (photo, overwrite) => {
        const id = photo.id ?? photo.photoId;
        if (!id)
            return;
        if (!overwrite && byId.has(id))
            return;
        byId.set(id, { id, promptKey: photo.promptKey });
    };
    for (const photo of primary)
        add(photo, true);
    for (const photo of fallback)
        add(photo, false);
    return [...byId.values()];
}
/** A linked photo id that exists, or a photo filed as `clause:<id>`. */
export function clauseHasEvidencePhoto(response, photos) {
    const ids = new Set();
    for (const photo of photos) {
        if (photo.id)
            ids.add(photo.id);
        if (photo.photoId)
            ids.add(photo.photoId);
    }
    if (response.photoIds.some((id) => ids.has(id)))
        return true;
    const clauseKey = `clause:${response.clauseId.trim()}`;
    return photos.some((photo) => photo.promptKey.trim() === clauseKey);
}
export const MissingClausePhotoSchema = z.object({
    section: ChecklistSectionSchema,
    clauseId: z.string().min(1),
    outcome: z.enum(['Compliant', 'Non-Compliant', 'Not Applicable']),
    assessment: z.string().optional(),
    assessmentLabel: z.string().optional(),
});
/**
 * Answered items with no photo, excluding Compliant, Not Sighted and Not
 * Applicable or N/V. Order follows the checklist.
 */
export function missingClausePhotos(checklist, photos) {
    const missing = [];
    for (const response of checklist) {
        if (clauseExemptFromMissingPhotoWarning(response))
            continue;
        if (clauseHasEvidencePhoto(response, photos))
            continue;
        const assessment = assessmentValueForClauseResponse(response);
        const assessmentLabel = assessment ? assessmentLabelForValue(assessment) : undefined;
        missing.push({
            section: response.section,
            clauseId: response.clauseId,
            outcome: response.outcome,
            ...(assessment ? { assessment } : {}),
            ...(assessmentLabel ? { assessmentLabel } : {}),
        });
    }
    return missing;
}
export function formatMissingClausePhoto(item) {
    const label = item.assessmentLabel ?? item.outcome;
    return `${item.clauseId} — ${label}`;
}
/**
 * Submit blocks for photo evidence.
 *
 * An answered mandatory-upload clause with no photo always blocks. Other
 * answered items block only when `REQUIRE_PHOTO_ON_EVERY_CHECKLIST_ITEM` is
 * on. Unanswered clauses stay on the outstanding list and do not block submit.
 */
export function mandatoryPhotoSubmitBlocks(draft, options) {
    const sections = options?.sections ?? [...sectionsCoveredBy(draft)];
    const photoIds = new Set(draft.photos.map((photo) => photo.id));
    const issues = [];
    const seenClauses = new Set();
    for (const entry of mandatoryChecklistPhotosFor(sections)) {
        const response = draft.checklist.find((row) => row.clauseId === entry.checklistItemId &&
            entry.appliesTo.includes(row.section) &&
            sections.includes(row.section));
        if (!response)
            continue;
        const satisfied = draft.photos.some((photo) => mandatoryPhotoSatisfiedBy(entry, photo.promptKey)) ||
            response.photoIds.some((id) => photoIds.has(id));
        if (satisfied)
            continue;
        const key = `${response.section}|${response.clauseId}`;
        if (seenClauses.has(key))
            continue;
        seenClauses.add(key);
        issues.push(`${entry.checklistItemId}: Capture required photo: ${entry.label}.`);
    }
    if (photoRequiredOnEveryChecklistItem(options)) {
        for (const response of draft.checklist) {
            if (!sections.includes(response.section))
                continue;
            const key = `${response.section}|${response.clauseId}`;
            if (seenClauses.has(key))
                continue;
            if (clauseHasEvidencePhoto(response, draft.photos))
                continue;
            seenClauses.add(key);
            issues.push(`${response.clauseId}: A photo is required for this item.`);
        }
    }
    return issues;
}
//# sourceMappingURL=photo-policy.js.map